/**
 * ProgramShell — V98-3-2 (PRD G-1 · ADR-0048 · D-98-1). Route:
 * /w/:slug/app/programs/:program/:tab? — the pattern every program page
 * copies.
 *
 *   header      crumbs · H1 · derived facts · VerdictBand (scoped counts
 *               from the shared counter, C20). A sweep program with a sweep
 *               shows its roll-up instead: in full on the Readiness view, as
 *               one chip to it on every other view (V109-5 BU-2).
 *   StageRail   Scope · Prepare · Audit · Certified[Operate ‖ Report]
 *               driven by the per-period stages from GET /programs. ONE
 *               filled cell = the open tab (the only aria-current); each
 *               active period is a pin on the station it sits in; N/A
 *               stations carry their reason (C19). Compact below 860px.
 *   tab panel   Scope: boundary (systems in/out, external services with
 *               coverage per period, derived gaps + actions). AI Regulatory
 *               (V98-4-5): Scope = obligation sources + decision vN editor +
 *               laws × systems grid + boundary; Operate = in-force gaps;
 *               Audit = Conformity (derived from the EU tiers). Prepare /
 *               Audit / Operate: SOC 2 readiness rehosted here; other
 *               programs get honest empty states with the next action.
 *               Report: hands off to Calendar & trust center (Track 5).
 *               Operate (V98-5-1): the operate queue per program — checks,
 *               recurring tasks, findings, acceptances, change notices with
 *               close rules, cadence staleness and deadline provenance.
 *
 * Absent programs render the same shell with an "Add program" state.
 * Focus moves to the panel heading when the open tab changes.
 *
 * Reads: GET /programs (any member), GET /today (viewer's lead programs).
 * Writes go through the panels (program lead or owner/admin; server-enforced).
 */
import { useEffect, useMemo, useRef } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { isSweepProgram } from '@aigovops/core';
import { ProgramRollup, RollupChip, RollupContext, rollupListHref, useRollupData } from '../components/sweep/ProgramRollup';
import { currentStage } from '../lib/sweep-model';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, EmptyState, PageHeader, Spinner, StageRail, VerdictBand } from '@aigovops/design-system';
import type { ProgramManifest } from '@aigovops/core';
import { useWorkspace } from '../lib/workspace';
import { usePrograms } from '../lib/workspace-navigation';
import { getToday } from '../lib/api';
import { getTodayPrograms } from '../lib/program-screens-api';
import { queryKeys } from '../lib/query-keys';
import { programTitle } from '../lib/nav-config';
import { CONFORMITY_PANEL, defaultTab, objectiveBandCounts, routeManifest, deriveStations, fmtProgramDate, isCertTab, programFacts, stationTitle, verdictCounts, type CertStage } from '../lib/program-shell';
import { AddProgramAction } from '../components/programs/AddProgramAction';
import { ProgramStart } from '../components/programs/ProgramStart';
import { ProgramFieldsLine } from '../components/programs/ProgramFields';
import { ProgramPanel } from '../components/programs/program-panels';
import { useNames } from './Today';
import { useNarrow } from '../lib/use-narrow';
import { cardStatus } from '../lib/today-model';
import { useAiGrid } from '../components/ai-program/AiProgramTabs';
import './programs.css';

/**
 * `resolveProgram` defaults to the registry (`routeManifest`: an active
 * manifest, or a planned one whose card the server returned — V102-8-1);
 * unknown keys, and planned ones without a card, go back to Programs. The
 * conformance test passes a manifest-only fake program through it (V102-1-6).
 */
export interface ProgramShellProps {
  resolveProgram?: (key: string | undefined, cards: ReadonlyArray<{ program: string }> | undefined) => ProgramManifest | 'pending' | undefined;
}

export function ProgramShell({ resolveProgram = routeManifest }: ProgramShellProps) {
  const { program: rawProgram, tab: rawTab } = useParams<{ program: string; tab?: string }>();
  const { workspace, role } = useWorkspace();
  const slug = workspace.slug, prefix = `/w/${slug}/app`;
  const navigate = useNavigate();
  const narrow = useNarrow();
  const programs = usePrograms(slug);
  const today = useQuery({ queryKey: queryKeys.today(slug), queryFn: () => getToday(slug), staleTime: 60_000 });
  const panelHeading = useRef<HTMLHeadingElement>(null);
  // V98-5 review ("Fix in Scope"): a page that sent the viewer here can be returned to (same-app paths only).
  const [sp] = useSearchParams();
  const ret = sp.get('return');
  const lastTab = useRef<string | null>(null);

  const resolved = resolveProgram(rawProgram, programs.data?.programs);
  const pending = resolved === 'pending';
  const m = pending ? undefined : resolved;
  const program = m?.key ?? null;
  const card = useMemo(() => programs.data?.programs.find((p) => p.program === program) ?? null, [programs.data, program]);
  // The Conformity station is derived from the AI grid's EU risk tiers.
  // V106-1-8 / V106-5-6 (build review round 2): SOC 2 / ISO 42001 on the objective engine take the band's verdict
  // and counts from the same server card as Today, so the header and Today never disagree.
  const objective = useQuery({ queryKey: ['today-programs', slug], queryFn: () => getTodayPrograms(slug), enabled: program === 'soc2' || program === 'iso-42001', retry: false, staleTime: 30_000 });
  const oc = card ? objective.data?.find((x) => x.profile_id === card.profile_id && x.engine === 'objective') ?? null : null;
  const aiGrid = useAiGrid(slug, !!m && Object.values(m.shell.panels).includes(CONFORMITY_PANEL) && !!card);
  const names = useNames(slug);
  const openTab: CertStage = isCertTab(rawTab) ? rawTab : m ? defaultTab(m, card) : 'scope';
  // V109-5-5: a sweep program's current stage (Readiness view) is headed by the roll-up — program readiness
  // from determinations with the sweep's change — once the workspace has a sweep; else the VerdictBand stays.
  const onCurrent = !!m && !!card && isSweepProgram(m) && openTab === currentStage(card) && sp.get('view') !== 'changes';
  // BU-2: every view of a sweep program reads the roll-up — the full one on Readiness, a chip elsewhere.
  const rollup = useRollupData(slug, m, card, !!m && !!card && isSweepProgram(m));
  const showRollup = onCurrent && !!rollup.data;
  const location = useLocation();
  // A count in the roll-up opens a list on this page (#anchor): focus its heading once it is there.
  const sweepPage = !!m && isSweepProgram(m);
  useEffect(() => {
    if (!location.hash || !sweepPage) return;
    const id = window.setTimeout(() => {
      const el = document.getElementById(decodeURIComponent(location.hash.slice(1)));
      if (!el) return;
      if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
      el.scrollIntoView?.({ block: 'start' }); el.focus();
    }, 50);
    return () => window.clearTimeout(id);
    // A legend count opens the same anchor with another `?status=`: the search is a dependency too (built-UI #3).
  }, [location.hash, location.pathname, location.search, programs.isLoading, showRollup, sweepPage]);

  // Focus the panel heading when the open tab changes (not on first render).
  useEffect(() => {
    if (programs.isLoading) return;
    if (lastTab.current !== null && lastTab.current !== openTab) panelHeading.current?.focus();
    lastTab.current = openTab;
  }, [openTab, programs.isLoading]);

  // A planned program waits for the cards: no card (or no preview) → back to Programs.
  if (pending) {
    return programs.error
      ? <div className="gp-program-shell"><Alert variant="error"><p>The program could not load.</p><Button onClick={() => void programs.refetch()}>Retry</Button></Alert></div>
      : <div className="gp-program-shell"><Spinner label="Loading program" /></div>;
  }
  if (!m || !program) return <Navigate to={`${prefix}/programs`} replace />;
  if (rawTab !== undefined && !isCertTab(rawTab)) return <Navigate to={`${prefix}/programs/${program}`} replace />;

  const title = card ? programTitle(m, card.report_type) : programTitle(m);
  const isAdmin = role === 'owner' || role === 'admin';
  const canManage = isAdmin || (!!card && !!today.data?.viewer.lead_program_ids.includes(card.profile_id));
  const stations = deriveStations(m, card, { conformity: aiGrid.data?.conformity ?? null });
  const tabHref = (t: CertStage) => `${prefix}/programs/${program}/${t}`;

  // StageRail / VerdictBand render plain anchors; keep navigation client-side.
  const onClientNav = (e: React.MouseEvent) => {
    const a = (e.target as HTMLElement).closest('a[href]') as HTMLAnchorElement | null;
    if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const href = a.getAttribute('href') ?? '';
    if (!href.startsWith('/')) return;
    e.preventDefault();
    navigate(href);
  };

  const facts = programFacts(card);
  const station = stations.find((s) => s.stage === openTab)!;

  return (
    <div className="gp-program-shell">
      <nav className="gp-crumbs" aria-label="Breadcrumb">
        {ret && ret.startsWith(`${prefix}/items/`) ? <><Link to={ret}>‹ Back to the change notice</Link><span aria-hidden="true">/</span></> : null}
        <Link to={`${prefix}/programs`}>Programs</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">{title}</span>
      </nav>

      {/* One header style across the product shell (PageHeader variant="app", V98-3 review U13). */}
      <PageHeader variant="app" title={title} headingId="program-h1" subtitle={facts.length ? facts.join(' · ') : undefined} />

      {programs.isLoading ? <Spinner label="Loading program" />
        : programs.error ? <Alert variant="error"><p>The program could not load.</p><Button onClick={() => void programs.refetch()}>Retry</Button></Alert>
        : <>
          {/* PR A: a scored program (CMMC) carries its own verdict, clock and counts in the scorecard; the generic
              strip (blocker-based "At risk · 0 open") would contradict it, so it is not shown there. */}
          {showRollup && card && rollup.data ? (
            <ProgramRollup slug={slug} prefix={prefix} m={m} card={card} data={rollup.data} canSweep={isAdmin}
              listHref={(k) => rollupListHref(prefix, m, card, k)} />
          ) : card && rollup.data ? (
            // BU-2: once a roll-up exists, no older verdict on the other views — one chip to Readiness.
            <RollupChip prefix={prefix} m={m} card={card} data={rollup.data} />
          ) : rollup.loading ? null : card && m.scoring.engine === 'none' && card.periods.some((p) => p.active) ? (
            <div onClick={onClientNav}>
              <VerdictBand
                verdict={oc ? (oc.status === 'off_track' ? 'off_track' : oc.status === 'at_risk' || oc.status === 'needs_setup' ? 'at_risk' : 'on_track') : card.program_status ?? cardStatus(card)}
                subject={title}
                counts={oc ? objectiveBandCounts(oc, card, prefix) : verdictCounts(card, prefix)}
                nextHardDate={card.next_date ? { label: card.next_date.what, date: fmtProgramDate(card.next_date.date), dateTime: card.next_date.date } : undefined}
              />
            </div>
          ) : null}

          <div className="gp-rail-card" onClick={onClientNav}>
            <StageRail
              label={`${title} stages`}
              loopLabel={m.shell.loopLabel ?? undefined}
              openTab={openTab}
              compact={narrow}
              stations={stations.map((s) => ({
                stage: s.stage,
                state: s.state,
                meta: s.meta,
                naReason: s.naReason,
                periods: s.periods,
                href: tabHref(s.stage),
              }))}
            />
          </div>

          {/* V108-8-15: the program's key fields (SOC 2 type, FedRAMP class, …) with Change for an owner / admin; under the
              status and the stage rail, so an open form never pushes the program's status down (glance review G-4). */}
          {card ? <ProgramFieldsLine slug={slug} profileId={card.profile_id} m={m} isAdmin={isAdmin} prefix={prefix} /> : null}
          {/* V108-8-14: a program added after setup opens on what existing sources already cover and its next step. */}
          {card && !card.periods.some((p) => p.active) ? <ProgramStart slug={slug} program={m.key} /> : null}

          <section className="gp-tab-panel" aria-labelledby="program-tab-title">
            <h2 id="program-tab-title" ref={panelHeading} tabIndex={-1}>{stationTitle(m, openTab)}</h2>
            {/* A derived N/A station with its own panel (Conformity) still renders the panel. */}
            {station.state === 'na' && !m.shell.panels[openTab]
              ? <EmptyState title={station.naReason} action={<Link to={tabHref(defaultTab(m, card))}>{`Go to ${stationTitle(m, defaultTab(m, card))}`}</Link>} />
              : !card
                // Absent program: every tab offers to start it (owner/admin), else ask an admin.
                ? <EmptyState title={`${programTitle(m)} is not set up`} action={<AddProgramAction slug={slug} program={m.key} manifest={m} canManage={isAdmin}
                    // A11y M (V108-8-15 review): after adding, focus lands on the program's heading, never on <body>.
                    onAdded={() => window.setTimeout(() => { const h = document.getElementById('program-h1'); if (h) { h.setAttribute('tabindex', '-1'); h.focus(); } }, 50)} />} />
                : <RollupContext.Provider value={showRollup && rollup.data ? { data: rollup.data, m, card } : null}><ProgramPanel tab={openTab} manifest={m} card={card} slug={slug} prefix={prefix} canManage={canManage} isAdmin={isAdmin} tabHref={tabHref}
                    viewerId={today.data?.viewer.user_id ?? ''} names={names} role={role ?? 'viewer'} /></RollupContext.Provider>}
          </section>
        </>}
    </div>
  );
}
